import genAI, { handleGeminiError } from '../ai/geminiClient';

/**
 * Semantic Search Service - AI-powered job search using Gemini
 * Provides intelligent search that understands context and intent
 */

/**
 * Generates search keywords and filters from natural language query using Gemini AI
 * @param {string} query - Natural language search query
 * @returns {Promise<Object>} Extracted search parameters
 */
export async function extractSearchIntent(query) {
  try {
    if (!query?.trim()) {
      return { keywords: [], filters: {}, confidence: 0 };
    }

    const model = genAI?.getGenerativeModel({ model: 'gemini-2.5-flash' });

    const prompt = `Analyze this job search query and extract structured information:
    
Query: "${query}"

Return a JSON object with:
{
  "keywords": ["key", "terms", "from", "query"],
  "jobTitle": "extracted job title if any",
  "location": { "city": "", "region": "", "country": "" },
  "workMode": "REMOTE|HYBRID|ONSITE|null",
  "employmentType": "FULL_TIME|PART_TIME|CONTRACT|null",
  "experienceLevel": "ENTRY|MID|SENIOR|null",
  "skills": ["skill1", "skill2"],
  "salary": { "min": 0, "max": 0, "currency": "USD" },
  "confidence": 0.0-1.0
}

Only include fields with actual values. Return valid JSON only.`;

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text();

    // Extract JSON from response
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch?.[0]);
    }

    return { keywords: [query], filters: {}, confidence: 0.5 };
  } catch (error) {
    const errorInfo = handleGeminiError(error);
    if (errorInfo?.isInternal) {
      console.log('Error in semantic search:', errorInfo?.message);
    } else {
      console.error('Error in semantic search:', error);
    }
    // Fallback to basic keyword extraction
    return {
      keywords: query?.split(' ')?.filter(w => w?.length > 2),
      filters: {},
      confidence: 0
    };
  }
}

/**
 * Performs semantic similarity search on jobs using AI embeddings
 * @param {string} query - Search query
 * @param {Array} jobs - Array of job objects
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Ranked job results
 */
export async function semanticJobSearch(query, jobs, options = {}) {
  try {
    const { limit = 20, includeReasons = true } = options;

    // Extract search intent using Gemini
    const searchIntent = await extractSearchIntent(query);

    // Filter jobs based on extracted filters
    let filteredJobs = filterJobsByIntent(jobs, searchIntent);

    // If we have keywords, rank by relevance
    if (searchIntent?.keywords?.length > 0 && includeReasons) {
      filteredJobs = await rankJobsByRelevance(
        query,
        filteredJobs,
        searchIntent
      );
    }

    return {
      results: filteredJobs?.slice(0, limit),
      intent: searchIntent,
      totalResults: filteredJobs?.length
    };
  } catch (error) {
    const errorInfo = handleGeminiError(error);
    if (errorInfo?.isInternal) {
      console.log('Error in semantic job search:', errorInfo?.message);
    } else {
      console.error('Error in semantic job search:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Filters jobs based on extracted search intent
 * @param {Array} jobs - Jobs to filter
 * @param {Object} intent - Extracted search intent
 * @returns {Array} Filtered jobs
 */
function filterJobsByIntent(jobs, intent) {
  return jobs?.filter(job => {
    // Filter by work mode
    if (intent?.filters?.workMode && job?.workMode !== intent?.filters?.workMode) {
      return false;
    }

    // Filter by employment type
    if (intent?.filters?.employmentType && job?.employmentType !== intent?.filters?.employmentType) {
      return false;
    }

    // Filter by location
    if (intent?.filters?.location) {
      const { city, region, country } = intent?.filters?.location;
      const hasLocation = job?.locations?.some(loc => {
        if (city && !loc?.city?.toLowerCase()?.includes(city?.toLowerCase())) return false;
        if (region && !loc?.region?.toLowerCase()?.includes(region?.toLowerCase())) return false;
        if (country && !loc?.country?.toLowerCase()?.includes(country?.toLowerCase())) return false;
        return true;
      });
      if (!hasLocation) return false;
    }

    // Filter by skills
    if (intent?.filters?.skills?.length > 0) {
      const hasSkills = intent?.filters?.skills?.some(skill =>
        job?.tags?.some(tag => tag?.toLowerCase()?.includes(skill?.toLowerCase()))
      );
      if (!hasSkills) return false;
    }

    // Filter by salary range
    if (intent?.filters?.salary) {
      const { min, max } = intent?.filters?.salary;
      if (min && job?.salary?.max < min) return false;
      if (max && job?.salary?.min > max) return false;
    }

    return true;
  });
}

/**
 * Ranks jobs by relevance using AI analysis
 * @param {string} query - Original search query
 * @param {Array} jobs - Filtered jobs
 * @param {Object} intent - Search intent
 * @returns {Promise<Array>} Ranked jobs with match reasons
 */
async function rankJobsByRelevance(query, jobs, intent) {
  try {
    // For each job, calculate a relevance score
    const scoredJobs = jobs?.map(job => {
      let score = 0;
      const reasons = [];

      // Title relevance
      if (intent?.jobTitle) {
        const titleMatch = job?.title?.toLowerCase()?.includes(intent?.jobTitle?.toLowerCase());
        if (titleMatch) {
          score += 30;
          reasons?.push(`Title matches "${intent?.jobTitle}"`);
        }
      }

      // Keyword relevance
      intent?.keywords?.forEach(keyword => {
        const keywordLower = keyword?.toLowerCase();
        if (job?.title?.toLowerCase()?.includes(keywordLower)) {
          score += 10;
          reasons?.push(`Title contains "${keyword}"`);
        }
        if (job?.companyName?.toLowerCase()?.includes(keywordLower)) {
          score += 5;
          reasons?.push(`Company name matches "${keyword}"`);
        }
        if (job?.tags?.some(tag => tag?.toLowerCase()?.includes(keywordLower))) {
          score += 8;
          reasons?.push(`Skills match "${keyword}"`);
        }
      });

      // Location match
      if (intent?.filters?.location) {
        score += 10;
        reasons?.push('Location matches your search');
      }

      // Work mode match
      if (intent?.filters?.workMode && job?.workMode === intent?.filters?.workMode) {
        score += 15;
        reasons?.push(`${job?.workMode} work mode`);
      }

      // Recency boost
      const daysSincePosted = (Date.now() - new Date(job.postedAt)?.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSincePosted < 7) {
        score += 10;
        reasons?.push('Recently posted');
      }

      return {
        ...job,
        matchScore: score,
        matchReasons: reasons?.slice(0, 3) // Top 3 reasons
      };
    });

    // Sort by score descending
    return scoredJobs?.sort((a, b) => b?.matchScore - a?.matchScore);
  } catch (error) {
    console.error('Error ranking jobs:', error);
    return jobs;
  }
}

/**
 * Generates search suggestions based on partial query
 * @param {string} partialQuery - Partial search query
 * @param {Array} recentSearches - Recent search history
 * @returns {Promise<Array>} Search suggestions
 */
export async function generateSearchSuggestions(partialQuery, recentSearches = []) {
  try {
    if (!partialQuery?.trim() || partialQuery?.length < 2) {
      return recentSearches?.slice(0, 5);
    }

    const model = genAI?.getGenerativeModel({ model: 'gemini-2.5-flash' });

    const prompt = `Generate 5 relevant job search suggestions based on: "${partialQuery}"

Consider:
- Common job titles
- Popular locations
- Work modes (remote, hybrid)
- Skills and technologies

Return only a JSON array of strings: ["suggestion 1", "suggestion 2", ...]`;

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text();

    // Extract JSON array
    const jsonMatch = text?.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      const suggestions = JSON.parse(jsonMatch?.[0]);
      
      // Combine with recent searches
      const combined = [
        ...suggestions,
        ...recentSearches?.filter(s => 
          s?.toLowerCase()?.includes(partialQuery?.toLowerCase())
        )
      ];

      // Remove duplicates and limit
      return [...new Set(combined)]?.slice(0, 5);
    }

    return recentSearches?.slice(0, 5);
  } catch (error) {
    const errorInfo = handleGeminiError(error);
    if (errorInfo?.isInternal) {
      console.log('Error generating suggestions:', errorInfo?.message);
    } else {
      console.error('Error generating suggestions:', error);
    }
    return recentSearches?.slice(0, 5);
  }
}